import React, { useMemo, useState } from "react";
import { Button, Input, Select, Skeleton, Table, Tag } from "antd";
import type { ColumnsType } from "antd/es/table";

import {
  Chip,
  fmtNum,
  MetricCard,
  SectionCard,
  TIER_COLOR,
  useMasteryApi,
} from "./shared";

// ── API ───────────────────────────────────────────────────────────────────────
// Ported from the standalone Golden Explorer's /api/networks router (see
// views_portfolio_mastery_networks.py). Directory and detail live in one
// surface: selecting a row opens the org, a back affordance returns.

interface DirectoryRow {
  institution_id: string;
  name: string | null;
  org_type: string | null;
  bucket: string;
  region: string | null;
  country: string | null;
  tier: string | null;
  member_sites: number;
  trial_count: number | null;
  parent: string | null;
  has_parent: boolean;
}

interface DirectoryPayload {
  results: DirectoryRow[];
  count: number;
  buckets: Array<{ bucket: string; n: number }>;
  stats: { total_institutions: number; networks: number; with_parent: number };
}

interface HierarchyNode {
  institution_id: string;
  name: string | null;
  org_type: string | null;
  country?: string | null;
  member_sites?: number;
  depth?: number;
}

interface MemberSite {
  golden_id: string;
  name: string | null;
  city: string | null;
  country: string | null;
  tier: string | null;
  confidence: number | null;
  merge_count: number | null;
  trials: number | null;
  link_method: string | null;
  similarity: number | null;
  link_status: string | null;
}

interface NetworkPerson {
  person_id: string;
  name: string | null;
  specialty: string | null;
  trial_count: number | null;
  sites_in_net: number;
}

interface DetailPayload {
  org: {
    institution_id: string;
    name: string | null;
    org_type: string | null;
    bucket: string;
    region: string | null;
    country: string | null;
    tier: string | null;
    canonical_address: string | null;
    phone: string | null;
    npi: string | null;
    external_org_id: string | null;
    trial_count: number | null;
    parent_institution_id: string | null;
    parent_name: string | null;
  };
  hierarchy: { parents: HierarchyNode[]; children: HierarchyNode[] };
  member_sites: MemberSite[];
  rollup: {
    sites: number;
    countries: Array<{ country: string | null; n: number }>;
    people_total: number;
  };
  people: NetworkPerson[];
}

// ── bucket vocabulary (mirrors the backend CASE) ──────────────────────────────

const BUCKET_LABEL: Record<string, string> = {
  network: "Network / SMO",
  academic: "Academic",
  hospital: "Hospital",
  other: "Other",
};
const BUCKET_COLOR: Record<string, string> = {
  network: "purple",
  academic: "blue",
  hospital: "default",
  other: "default",
};
const BUCKET_ORDER = ["network", "academic", "hospital", "other"];

function BucketTag({
  bucket,
  orgType,
}: {
  bucket: string;
  orgType?: string | null;
}) {
  return (
    <Tag color={BUCKET_COLOR[bucket] ?? "default"} title={orgType ?? undefined}>
      {BUCKET_LABEL[bucket] ?? bucket}
    </Tag>
  );
}

const dash = (v: string | null | undefined) => (v ? v : "—");
const pct = (n: number, total: number) =>
  total > 0 ? `${((100 * n) / total).toFixed(1)}%` : "—";

const PAGE_SIZE = 50;

// ── detail pane ───────────────────────────────────────────────────────────────

const MEMBER_COLS: ColumnsType<MemberSite> = [
  {
    key: "name",
    title: "Golden site",
    dataIndex: "name",
    render: (v: string | null, r) =>
      v || <span className="text-[#9aa9b0]">(nameless) {r.golden_id}</span>,
  },
  { key: "city", title: "City", dataIndex: "city", width: 160 },
  { key: "country", title: "Country", dataIndex: "country", width: 100 },
  {
    key: "tier",
    title: "Tier",
    dataIndex: "tier",
    width: 130,
    render: (v: string | null) =>
      v ? <Tag color={TIER_COLOR[v] ?? "default"}>{v}</Tag> : "—",
  },
  {
    key: "confidence",
    title: "Confidence",
    dataIndex: "confidence",
    width: 105,
    align: "right",
    render: (v: number | null) => (v == null ? "—" : v.toFixed(2)),
  },
  {
    key: "merge_count",
    title: "Merged",
    dataIndex: "merge_count",
    width: 85,
    align: "right",
  },
  {
    key: "trials",
    title: "Trials",
    dataIndex: "trials",
    width: 75,
    align: "right",
  },
  {
    key: "link_method",
    title: "Link",
    dataIndex: "link_method",
    width: 200,
    render: (v: string | null, r) => (
      <span className="flex items-center gap-1">
        <Chip>{dash(v)}</Chip>
        {r.similarity != null && (
          <span className="text-xs tabular-nums text-[#2B4752]">
            {r.similarity.toFixed(2)}
          </span>
        )}
      </span>
    ),
  },
  {
    key: "link_status",
    title: "Link status",
    dataIndex: "link_status",
    width: 110,
    render: (v: string | null) =>
      v ? <Tag color={v === "committed" ? "green" : "default"}>{v}</Tag> : "—",
  },
];

const PEOPLE_COLS: ColumnsType<NetworkPerson> = [
  {
    key: "name",
    title: "Name",
    dataIndex: "name",
    render: (v: string | null, r) => v || r.person_id,
  },
  {
    key: "sites_in_net",
    title: "Member sites",
    dataIndex: "sites_in_net",
    width: 120,
    align: "right",
    render: (v: number) =>
      v > 1 ? <Tag color="purple">{v} sites</Tag> : <span>{v}</span>,
  },
  { key: "specialty", title: "Specialty", dataIndex: "specialty" },
  {
    key: "trial_count",
    title: "Trials",
    dataIndex: "trial_count",
    width: 85,
    align: "right",
  },
];

function HierarchyList({
  data,
  orgName,
}: {
  data: DetailPayload;
  orgName: string;
}) {
  // parents arrive immediate-parent-first; render root-first so the chain
  // reads top-down, then the org itself, then its children one level in.
  const rootFirst = [...data.hierarchy.parents].reverse();
  const orgIndent = rootFirst.length;
  return (
    <div>
      {rootFirst.map((p, i) => (
        <div
          key={p.institution_id}
          className="border-l-2 border-[#E6EEF2] py-0.5 text-sm text-[#2B4752]"
          style={{ marginLeft: i * 16, paddingLeft: 12 }}
        >
          {dash(p.name)}
          <span className="ml-2 text-xs text-[#556C75]">
            {dash(p.org_type)}
          </span>
        </div>
      ))}
      <div
        className="border-l-2 border-[#2B4752] py-0.5 text-sm font-semibold text-[#2B4752]"
        style={{ marginLeft: orgIndent * 16, paddingLeft: 12 }}
      >
        {orgName}
      </div>
      {data.hierarchy.children.map((c) => (
        <div
          key={c.institution_id}
          className="border-l-2 border-[#E6EEF2] py-0.5 text-sm text-[#2B4752]"
          style={{ marginLeft: (orgIndent + 1) * 16, paddingLeft: 12 }}
        >
          {dash(c.name)}
          <span className="ml-2 text-xs tabular-nums text-[#556C75]">
            {fmtNum(c.member_sites ?? 0)} sites
          </span>
        </div>
      ))}
    </div>
  );
}

function NetworkDetailPane({
  orgId,
  onBack,
}: {
  orgId: string;
  onBack: () => void;
}) {
  const { data, isLoading } = useMasteryApi<DetailPayload>(
    `mastery-networks/${orgId}/`,
  );

  if (isLoading || !data) {
    return (
      <div>
        <Button size="small" onClick={onBack}>
          ← All networks
        </Button>
        <div className="mt-4">
          <Skeleton active paragraph={{ rows: 10 }} />
        </div>
      </div>
    );
  }

  const org = data.org;
  const name = org.name ?? org.institution_id;
  const multiSitePeople = data.people.filter((p) => p.sites_in_net > 1).length;
  const goldenMembers = data.member_sites.filter(
    (m) => m.tier === "GOLDEN",
  ).length;

  return (
    <div>
      <div className="mb-4 flex items-center gap-3">
        <Button size="small" onClick={onBack}>
          ← All networks
        </Button>
        <span className="text-lg font-semibold text-[#2B4752]">{name}</span>
        <BucketTag bucket={org.bucket} orgType={org.org_type} />
        {org.tier && <Tag>{org.tier}</Tag>}
        {org.country && <Tag>{org.country}</Tag>}
      </div>

      <div className="mb-4 grid grid-cols-4 gap-4">
        <MetricCard
          label="Member sites"
          value={fmtNum(data.rollup.sites)}
          rows={[
            { label: "Golden tier", value: fmtNum(goldenMembers) },
            {
              label: "Provisional",
              value: fmtNum(data.rollup.sites - goldenMembers),
            },
          ]}
        />
        <MetricCard
          label="Countries"
          value={fmtNum(data.rollup.countries.length)}
          rows={data.rollup.countries.slice(0, 3).map((c) => ({
            label: c.country ?? "—",
            value: fmtNum(c.n),
          }))}
        />
        <MetricCard
          label="People across sites"
          value={fmtNum(data.rollup.people_total)}
          rows={[
            { label: "Spanning 2+ sites", value: fmtNum(multiSitePeople) },
          ]}
        />
        <MetricCard
          label="Hierarchy"
          value={fmtNum(
            data.hierarchy.parents.length + data.hierarchy.children.length,
          )}
          rows={[
            { label: "Parents", value: fmtNum(data.hierarchy.parents.length) },
            {
              label: "Children",
              value: fmtNum(data.hierarchy.children.length),
            },
          ]}
        />
      </div>

      <SectionCard title="Organization">
        <div className="grid grid-cols-[repeat(auto-fit,minmax(260px,1fr))] gap-x-7 gap-y-1">
          {[
            { label: "Institution ID", value: org.institution_id },
            { label: "Raw org type", value: dash(org.org_type) },
            { label: "Region", value: dash(org.region) },
            { label: "Address", value: dash(org.canonical_address) },
            { label: "Phone", value: dash(org.phone) },
            { label: "NPI", value: dash(org.npi) },
            { label: "External org ID", value: dash(org.external_org_id) },
            {
              label: "Trials",
              value: org.trial_count == null ? "—" : fmtNum(org.trial_count),
            },
          ].map((r) => (
            <div
              key={r.label}
              className="flex items-baseline justify-between py-0.5"
            >
              <span className="text-sm text-[#2B4752]">{r.label}</span>
              <span className="text-sm font-semibold tabular-nums text-[#2B4752]">
                {r.value}
              </span>
            </div>
          ))}
        </div>
      </SectionCard>

      <SectionCard title="Hierarchy">
        {data.hierarchy.parents.length || data.hierarchy.children.length ? (
          <HierarchyList data={data} orgName={name} />
        ) : (
          <p className="text-sm text-[#2B4752]">
            No parent or child organizations.
          </p>
        )}
      </SectionCard>

      <SectionCard
        title="Member golden sites"
        meta={`${fmtNum(data.rollup.sites)} sites`}
      >
        <Table<MemberSite>
          size="small"
          rowKey="golden_id"
          columns={MEMBER_COLS}
          dataSource={data.member_sites}
          pagination={{ pageSize: 15, showSizeChanger: false }}
          scroll={{ y: 420 }}
        />
      </SectionCard>

      <SectionCard
        title="People across the network"
        meta={`${fmtNum(data.rollup.people_total)} people`}
      >
        {data.people.length ? (
          <Table<NetworkPerson>
            size="small"
            rowKey="person_id"
            columns={PEOPLE_COLS}
            dataSource={data.people}
            pagination={{ pageSize: 10, showSizeChanger: false }}
          />
        ) : (
          <p className="text-sm text-[#2B4752]">
            No people linked across member sites.
          </p>
        )}
      </SectionCard>
    </div>
  );
}

// ── directory ─────────────────────────────────────────────────────────────────

export default function MasteryNetworks() {
  const [selected, setSelected] = useState<string | null>(null);
  const [q, setQ] = useState("");
  const [bucket, setBucket] = useState<string>("");
  const [page, setPage] = useState(1);

  const { data, isLoading } = useMasteryApi<DirectoryPayload>(
    "mastery-networks/",
    {
      q: q || undefined,
      bucket: bucket || undefined,
      limit: PAGE_SIZE,
      offset: (page - 1) * PAGE_SIZE,
    },
  );

  const columns: ColumnsType<DirectoryRow> = useMemo(
    () => [
      {
        key: "name",
        title: "Organization",
        dataIndex: "name",
        render: (v: string | null, r) => (
          <span className="font-medium text-[#2B4752]">
            {v || r.institution_id}
          </span>
        ),
      },
      {
        key: "bucket",
        title: "Type",
        dataIndex: "bucket",
        width: 150,
        render: (v: string, r) => <BucketTag bucket={v} orgType={r.org_type} />,
      },
      { key: "country", title: "Country", dataIndex: "country", width: 100 },
      {
        key: "tier",
        title: "Tier",
        dataIndex: "tier",
        width: 110,
        render: (v: string | null) =>
          v ? (
            <Tag color={v === "committed" ? "green" : "default"}>{v}</Tag>
          ) : (
            "—"
          ),
      },
      {
        key: "member_sites",
        title: "Member sites",
        dataIndex: "member_sites",
        width: 120,
        align: "right",
        render: (v: number) => (
          <span className="tabular-nums">{fmtNum(v)}</span>
        ),
      },
      {
        key: "trial_count",
        title: "Trials",
        dataIndex: "trial_count",
        width: 85,
        align: "right",
        render: (v: number | null) => (v == null ? "—" : fmtNum(v)),
      },
      { key: "parent", title: "Parent", dataIndex: "parent", render: dash },
    ],
    [],
  );

  if (selected) {
    return (
      <NetworkDetailPane orgId={selected} onBack={() => setSelected(null)} />
    );
  }

  const total = data?.stats.total_institutions ?? 0;
  const bucketCounts = new Map(
    (data?.buckets ?? []).map((b) => [b.bucket, b.n]),
  );

  return (
    <div>
      <div className="mb-4 grid grid-cols-5 gap-4">
        <MetricCard
          label="Institutions"
          value={fmtNum(total)}
          rows={[
            {
              label: "Networks / SMOs",
              value: fmtNum(data?.stats.networks ?? 0),
            },
            {
              label: "Under a parent",
              value: fmtNum(data?.stats.with_parent ?? 0),
            },
          ]}
        />
        {BUCKET_ORDER.map((b) => {
          const n = bucketCounts.get(b) ?? 0;
          return (
            <MetricCard
              key={b}
              label={BUCKET_LABEL[b]}
              value={fmtNum(n)}
              rows={[{ label: "Share", value: pct(n, total) }]}
            />
          );
        })}
      </div>

      <SectionCard
        title="Institutions & networks"
        meta={data ? `${fmtNum(data.count)} matching` : undefined}
        extra={
          <span className="flex items-center gap-2">
            <Select
              size="small"
              value={bucket}
              style={{ width: 150 }}
              onChange={(v) => {
                setBucket(v);
                setPage(1);
              }}
              options={[
                { label: "All types", value: "" },
                ...BUCKET_ORDER.map((b) => ({
                  label: BUCKET_LABEL[b],
                  value: b,
                })),
              ]}
            />
            <Input.Search
              size="small"
              allowClear
              placeholder="Name or ID"
              style={{ width: 220 }}
              onSearch={(v) => {
                setQ(v.trim());
                setPage(1);
              }}
            />
          </span>
        }
      >
        <Table<DirectoryRow>
          size="small"
          rowKey="institution_id"
          loading={isLoading}
          columns={columns}
          dataSource={data?.results ?? []}
          onRow={(r) => ({
            onClick: () => setSelected(r.institution_id),
            className: "cursor-pointer",
          })}
          pagination={{
            current: page,
            pageSize: PAGE_SIZE,
            total: data?.count ?? 0,
            showSizeChanger: false,
            onChange: setPage,
          }}
          scroll={{ y: 480 }}
        />
      </SectionCard>
    </div>
  );
}
